<script setup>
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import { fetchCsrfBootstrap } from '@/api/auth.js';
import { api } from '@/api/client.js';
import Badge from '@/components/ds/Badge.vue';
import Button from '@/components/ds/Button.vue';
import Icon from '@/components/ds/Icon.vue';
import ErrorIssueReport from '@/components/panels/ErrorIssueReport.vue';
import ErrorTechnicalDetails from '@/components/panels/ErrorTechnicalDetails.vue';
import ListPager from '@/components/panels/ListPager.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { useListPage } from '@/composables/useListPage.js';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useAuthStore } from '@/stores/auth.js';
import { useTenantStore } from '@/stores/tenant.js';

const STATUS_LABEL = { open: 'Open', acknowledged: 'Acknowledged', resolved: 'Resolved', ignored: 'Ignored' };
const props = defineProps({ focusFingerprint: { type: String, default: '' } });
const tenant = useTenantStore();
const auth = useAuthStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
const groups = ref([]);
const linkedHistoricalFingerprint = ref('');
const recentGroupCount = ref(0);
const search = ref('');
const status = ref('all');
const expandedFingerprint = ref(null);
const environments = ref(new Map());
const splitChoice = ref('page_path');
const splitAcknowledged = ref(false);
const splitBusy = ref(false);
const splitError = ref('');
let contextVersion = 0;
let focusedFingerprint = '';
const requests = new Map();
const filtered = computed(() => {
  const term = search.value.trim().toLowerCase();
  return groups.value.filter(g => (status.value === 'all' || g.status === status.value)
    && [g.sampleMessage, g.moduleKey, g.fingerprint].some(value => String(value ?? '').toLowerCase().includes(term)));
});
const { page, visible, pageSize } = useListPage(filtered);
watch([search, status], () => { page.value = 1; expandedFingerprint.value = null; });
watch(page, () => { expandedFingerprint.value = null; });
watch(() => props.focusFingerprint, fingerprint => {
  focusedFingerprint = '';
  if (!fingerprint) search.value = '';
});
watch([groups, () => props.focusFingerprint], async () => {
  const fingerprint = props.focusFingerprint;
  if (!fingerprint || fingerprint === focusedFingerprint || !groups.value.some(g => g.fingerprint === fingerprint)) return;
  const version = contextVersion;
  focusedFingerprint = fingerprint;
  search.value = fingerprint;
  status.value = 'all';
  page.value = 1;
  await nextTick();
  if (version !== contextVersion || props.focusFingerprint !== fingerprint) return;
  toggleDetails(fingerprint);
  await nextTick();
  document.getElementById(`error-details-${fingerprint}`)?.scrollIntoView?.({ block: 'center' });
});

async function fetchFn({ signal }) {
  const appId = activeAppId.value;
  if (!appId) return;
  // A route can switch the linked error while a poll is already in flight.
  // Publish only the latest requested fingerprint within this app context.
  while (!signal.aborted && appId === activeAppId.value) {
    const fingerprint = props.focusFingerprint;
    try {
      const r = await api.events.errorGroups({ appId, window: timeWindow.value, signal });
      if (!r || r.ok !== true) throw new Error('Invalid error-groups response');
      const rows = r.errorGroups ?? [];
      let historical = null;
      if (/^[a-f0-9]{64}$/.test(fingerprint) && !rows.some(g => g.fingerprint === fingerprint)) {
        if (signal.aborted || appId !== activeAppId.value) return;
        if (fingerprint !== props.focusFingerprint) continue;
        const linked = await api.events.errorGroups({ appId, fingerprint, signal });
        if (!linked?.ok) throw new Error('Invalid linked error response');
        historical = linked.errorGroups?.find(g => g.fingerprint === fingerprint && g.appId === appId) ?? null;
      }
      if (fingerprint !== props.focusFingerprint) continue;
      return { groups: historical ? [...rows, historical] : rows, recentCount: rows.length,
        historicalFingerprint: historical?.fingerprint ?? '', appId, fingerprint };
    } catch (err) {
      if (fingerprint !== props.focusFingerprint && !signal.aborted) continue;
      throw err;
    }
  }
}
const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey, () => props.focusFingerprint],
  onSuccess(result) {
    if (result?.appId !== activeAppId.value || result?.fingerprint !== props.focusFingerprint) return;
    groups.value = result.groups;
    recentGroupCount.value = result.recentCount;
    linkedHistoricalFingerprint.value = result.historicalFingerprint;
    if (!groups.value.some(g => g.fingerprint === expandedFingerprint.value)) expandedFingerprint.value = null;
    ps.setState(groups.value.length ? 'data' : 'empty');
  },
  onError(err) { ps.setState('error', { message: "Couldn't load error groups", detail: err?.message ?? '' }); },
});
function clearRequests() {
  contextVersion++;
  for (const controller of requests.values()) controller.abort();
  requests.clear();
}
watch(() => tenant.changeKey, () => {
  clearRequests();
  groups.value = [];
  linkedHistoricalFingerprint.value = '';
  recentGroupCount.value = 0;
  environments.value = new Map();
  expandedFingerprint.value = null;
  focusedFingerprint = '';
  page.value = 1;
  search.value = '';
  status.value = 'all';
  ps.setState('loading');
});
onBeforeUnmount(clearRequests);
ps.setRetryHandler(() => tick());
defineExpose({ ps, refreshStatus });

function formatDate(iso) {
  const date = new Date(iso);
  return iso && Number.isFinite(date.getTime()) ? date.toLocaleString() : '—';
}
async function loadEnvironments(fingerprint) {
  if (requests.has(fingerprint)) return;
  const version = contextVersion;
  const controller = new AbortController();
  requests.set(fingerprint, controller);
  environments.value.set(fingerprint, { state: 'loading', rows: [] });
  try {
    const r = await api.events.errorEnvironments({ fingerprint, signal: controller.signal });
    if (!r?.ok) throw new Error('Invalid environments response');
    if (version === contextVersion) environments.value.set(fingerprint, { state: 'data', rows: r.environments ?? [] });
  } catch {
    if (version === contextVersion) environments.value.set(fingerprint, { state: 'error', rows: [] });
  } finally {
    if (requests.get(fingerprint) === controller) requests.delete(fingerprint);
  }
}
function toggleDetails(fingerprint) {
  expandedFingerprint.value = expandedFingerprint.value === fingerprint ? null : fingerprint;
  splitChoice.value = 'page_path';
  splitAcknowledged.value = false;
  splitError.value = '';
  if (expandedFingerprint.value && !environments.value.has(fingerprint)) loadEnvironments(fingerprint);
}
async function splitGroup(group) {
  if (!auth.isAdmin || splitBusy.value || !splitAcknowledged.value) return;
  splitBusy.value = true;
  splitError.value = '';
  try {
    const csrf = (await fetchCsrfBootstrap()).csrfToken;
    await api.admin.splitErrorGroup({ appId:activeAppId.value,fingerprint:group.fingerprint,
      splitBy:splitChoice.value,csrf });
    splitAcknowledged.value = false;
    await tick();
  } catch (error) {
    splitError.value = error?.message ?? 'Could not create the split rule.';
  } finally { splitBusy.value = false; }
}
</script>

<template>
  <PanelCard
    title="Error groups"
    :state="ps.state.value"
    :empty-headline="focusFingerprint ? 'The linked error could not be found for this app.' : 'No error groups in this window.'"
    :empty-body="focusFingerprint ? 'It may have been removed by retention.' : 'Nothing to triage yet — errors will appear here as they are recorded.'"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <div class="error-groups__toolbar">
      <label class="error-groups__search">Search errors
        <input
          v-model="search"
          type="search"
          placeholder="Message, module, or fingerprint"
        >
      </label>
      <label>Status
        <select v-model="status">
          <option value="all">All statuses</option>
          <option
            v-for="(label, key) in STATUS_LABEL"
            :key="key"
            :value="key"
          >{{ label }}</option>
        </select>
      </label>
    </div>
    <p
      v-if="focusFingerprint && !groups.some(g => g.fingerprint === focusFingerprint)"
      class="error-groups__hint"
      role="status"
    >
      The linked error could not be found for this app. It may have been removed by retention.
    </p>
    <p
      v-if="linkedHistoricalFingerprint"
      class="error-groups__hint"
      role="status"
      data-test="linked-historical-error"
    >
      Showing the linked error from history, outside this period or the latest 100 groups. Its occurrence count is a lifetime total.
    </p>
    <p class="error-groups__hint">
      Latest {{ recentGroupCount }} groups with activity in this period · occurrence counts are lifetime totals.
    </p>
    <p
      v-if="recentGroupCount >= 100"
      class="error-groups__hint"
    >
      Limited to the 100 most recently active groups. Search and filters apply to this selection.
    </p>
    <ul class="error-groups__list">
      <li
        v-for="g in visible"
        :key="g.fingerprint"
        class="error-groups__item"
        data-test="error-group-row"
      >
        <button
          type="button"
          class="error-groups__toggle"
          data-test="error-group-environments-toggle"
          :aria-expanded="expandedFingerprint === g.fingerprint"
          :aria-controls="`error-details-${g.fingerprint}`"
          @click="toggleDetails(g.fingerprint)"
        >
          <Icon :name="expandedFingerprint === g.fingerprint ? 'chevUp' : 'chevDown'" />
          <span class="error-groups__headline">
            <span class="error-groups__message">{{ g.sampleMessage || 'Error without a recorded message' }}</span>
            <span class="error-groups__hint">{{ g.moduleKey || 'Unassigned module' }} · {{ formatDate(g.lastSeenAt) }}</span>
          </span>
          <span class="error-groups__badges">
            <Badge :tone="g.status === 'resolved' ? 'success' : 'neutral'">{{ STATUS_LABEL[g.status] ?? g.status }}</Badge>
            <Badge
              tone="neutral"
              title="Severity has not been assessed"
            >Unrated</Badge>
          </span>
          <span class="error-groups__count">{{ g.occurrences.toLocaleString() }}<span>occurrences</span></span>
        </button>
        <div
          v-if="expandedFingerprint === g.fingerprint"
          :id="`error-details-${g.fingerprint}`"
          class="error-groups__details"
        >
          <h3>Recorded message</h3>
          <p class="error-groups__full-message">
            {{ g.sampleMessage }}
          </p>
          <dl class="error-groups__meta">
            <div><dt>First seen</dt><dd>{{ formatDate(g.firstSeenAt) }}</dd></div>
            <div><dt>Last seen</dt><dd>{{ formatDate(g.lastSeenAt) }}</dd></div>
            <div><dt>Event type</dt><dd>{{ g.eventType || '—' }}</dd></div>
            <div><dt>Observed environments</dt><dd>{{ g.environments?.length ? g.environments.join(', ') : 'Unknown (legacy or unlabelled)' }}</dd></div>
            <div
              v-for="env in g.environmentStats || []"
              :key="env.environment"
            >
              <dt>{{ env.environment }}</dt><dd>{{ env.occurrences }} labelled occurrences</dd>
            </div>
            <div><dt>Severity</dt><dd>Not assessed</dd></div>
            <div><dt>First release</dt><dd>{{ g.firstReleaseSha || '—' }}</dd></div>
            <div><dt>Latest release</dt><dd>{{ g.lastReleaseSha || '—' }}</dd></div>
            <div class="error-groups__fingerprint">
              <dt>Fingerprint</dt><dd>{{ g.fingerprint }}</dd>
            </div>
            <div v-if="g.baseFingerprint">
              <dt>Original fingerprint</dt><dd>{{ g.baseFingerprint }}</dd>
            </div>
          </dl>
          <p
            v-if="g.activeSplitBy"
            class="error-groups__hint"
            role="status"
          >
            Future occurrences are split by {{ g.activeSplitBy === 'page_path' ? 'page path' : 'fourth stack frame' }} since {{ formatDate(g.splitActivatedAt) }}. Historical totals stay in this group.
          </p>
          <div
            v-else-if="auth.isAdmin && !g.baseFingerprint"
            class="error-groups__split"
          >
            <h3>Split an over-broad group</h3>
            <label>Distinguish future occurrences by
              <select v-model="splitChoice">
                <option value="page_path">Page path</option>
                <option value="fourth_stack_frame">Fourth stack frame</option>
              </select>
            </label>
            <label><input
              v-model="splitAcknowledged"
              type="checkbox"
            > Existing counts remain in the original group; only future events are split.</label>
            <Button
              size="sm"
              :disabled="!splitAcknowledged"
              :loading="splitBusy"
              @click="splitGroup(g)"
            >
              Apply split rule
            </Button>
            <p
              v-if="splitError"
              role="alert"
            >
              {{ splitError }}
            </p>
          </div>
          <ErrorIssueReport
            v-if="auth.isAdmin"
            :app-id="activeAppId"
            :fingerprint="g.fingerprint"
          />
          <ErrorTechnicalDetails
            :key="tenant.changeKey + g.fingerprint"
            :app-id="activeAppId"
            :fingerprint="g.fingerprint"
            :window="timeWindow"
          />
          <h3>Environment activity</h3>
          <div
            data-test="error-group-environments"
            aria-live="polite"
          >
            <p
              v-if="environments.get(g.fingerprint)?.state === 'loading'"
              class="error-groups__hint"
            >
              Loading environments…
            </p>
            <div
              v-else-if="environments.get(g.fingerprint)?.state === 'error'"
              class="error-groups__retry"
            >
              <span>Could not load environments.</span>
              <Button
                variant="secondary"
                size="sm"
                @click="loadEnvironments(g.fingerprint)"
              >
                Retry environments
              </Button>
            </div>
            <ul
              v-else-if="environments.get(g.fingerprint)?.rows.length"
              class="error-groups__environments"
            >
              <li
                v-for="e in environments.get(g.fingerprint).rows"
                :key="e.appId"
              >
                <span>{{ e.appId }} · {{ e.environmentStats?.length ? e.environmentStats.map(x => `${x.environment}: ${x.occurrences}`).join(', ') : 'environment unknown' }}</span><span>{{ e.occurrences }} lifetime occurrences</span>
              </li>
            </ul>
            <p
              v-else
              class="error-groups__hint"
            >
              No environment activity recorded.
            </p>
          </div>
        </div>
      </li>
    </ul>
    <p
      v-if="!filtered.length"
      class="error-groups__no-results"
      role="status"
    >
      No errors match your search and status filter.
    </p>
    <ListPager
      v-model:page="page"
      :total="filtered.length"
      :page-size="pageSize"
      label="Error groups pagination"
    />
    <template #tableFallback>
      <table class="error-groups__table">
        <caption class="visually-hidden">
          Error groups — current page
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Module
            </th><th scope="col">
              Status
            </th><th scope="col">
              Lifetime occurrences
            </th><th scope="col">
              Last seen
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="g in visible"
            :key="g.fingerprint"
          >
            <td>{{ g.moduleKey }}</td><td>{{ STATUS_LABEL[g.status] ?? g.status }}</td><td>{{ g.occurrences }}</td><td>{{ formatDate(g.lastSeenAt) }}</td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.error-groups__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
}

.error-groups__toolbar label {
  display: grid;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--fg-secondary);
}

.error-groups__search {
  flex: 1;
  min-width: 12rem;
}

.error-groups__toolbar input,
.error-groups__toolbar select {
  min-height: var(--control-md);
  padding: var(--space-2) var(--space-3);
  background: var(--bg-canvas);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--fg-primary);
  font: inherit;
}

.error-groups__hint {
  margin: 0;
  color: var(--fg-secondary);
  font-size: var(--text-xs);
}

.error-groups__list {
  list-style: none;
  padding: 0;
  margin: var(--space-4) 0 0;
  display: grid;
  gap: var(--space-2);
}

.error-groups__item {
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-lg);
}

.error-groups__toggle {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-4);
  background: transparent;
  border: 0;
  border-radius: var(--radius-lg);
  color: var(--fg-primary);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.error-groups__toggle:hover,
.error-groups__toggle[aria-expanded="true"] {
  background: var(--bg-subtle);
}

.error-groups__toggle:focus-visible {
  outline: var(--bw-thick) solid var(--accent-border);
  outline-offset: var(--space-1);
}

.error-groups__toggle>svg {
  flex-shrink: 0;
}

.error-groups__headline {
  display: grid;
  gap: var(--space-1);
  flex: 1;
  min-width: 0;
}

.error-groups__message {
  font-size: var(--text-sm);
  font-weight: var(--fw-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.error-groups__badges {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.error-groups__count {
  display: grid;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: var(--fw-semibold);
}

.error-groups__count span {
  font-size: var(--text-xs);
  font-weight: var(--fw-regular);
  color: var(--fg-secondary);
}

.error-groups__details {
  padding: var(--space-5);
  border-top: var(--bw-thin) solid var(--border-default);
  overflow-wrap: anywhere;
}

.error-groups__details h3 {
  margin: 0 0 var(--space-2);
  font-size: var(--text-sm);
}

.error-groups__full-message {
  margin: 0;
  padding: var(--space-3);
  background: var(--bg-subtle);
  border-radius: var(--radius-md);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  white-space: pre-wrap;
}

.error-groups__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-4);
  margin: var(--space-5) 0;
}

.error-groups__meta dt {
  color: var(--fg-secondary);
  font-size: var(--text-xs);
}

.error-groups__meta dd {
  margin: var(--space-1) 0 0;
  font-size: var(--text-sm);
}

.error-groups__fingerprint {
  grid-column: 1 / -1;
}

.error-groups__environments {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: var(--space-2);
}

.error-groups__environments li,
.error-groups__retry {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: var(--space-3);
  background: var(--bg-subtle);
  border-radius: var(--radius-md);
  font-size: var(--text-xs);
}

.error-groups__no-results {
  padding: var(--space-5);
  color: var(--fg-secondary);
  text-align: center;
}

.error-groups__table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.error-groups__table th,
.error-groups__table td {
  padding: var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
  overflow-wrap: anywhere;
}

@media (width <=1023px) {
  .error-groups__toggle {
    flex-wrap: wrap;
  }

  .error-groups__headline {
    flex-basis: 80%;
  }

  .error-groups__count {
    margin-left: auto;
  }
}
</style>
